import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  AlertCircle, 
  RefreshCw, 
  Trash2,
  Settings 
} from "lucide-react";
import { Integration, IntegrationProvider } from "@/hooks/useIntegrations";
import { IntegrationStatus } from "./IntegrationStatus";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface IntegrationCardProps {
  name: string;
  description: string;
  provider: IntegrationProvider;
  logoUrl: string;
  integration?: Integration;
  onConnect: () => void;
  onDisconnect: (id: string) => void;
  onDelete: (id: string) => void;
  onSync: (id: string) => void;
  onConfigure: () => void;
  isAvailable?: boolean;
}

export function IntegrationCard({
  name,
  description,
  provider,
  logoUrl,
  integration,
  onConnect,
  onDisconnect,
  onDelete,
  onSync,
  onConfigure,
  isAvailable = true,
}: IntegrationCardProps) {
  const isConnected = integration?.status === "connected";
  const hasError = integration?.status === "error";

  return (
    <TooltipProvider>
      <Card className="p-6 transition-smooth hover:shadow-primary-md">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4 flex-1">
          <div className="rounded-lg bg-card border p-4 flex items-center justify-center min-w-[64px]">
            <img src={logoUrl} alt={`${name} logo`} className="h-12 w-12 object-contain" />
          </div>
          <div className="space-y-2 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold">{name}</h3>
              {integration && <IntegrationStatus status={integration.status} />}
            </div>
            <p className="text-sm text-muted-foreground">{description}</p>
            
            {integration?.last_sync_at && (
              <p className="text-xs text-muted-foreground">
                Última sincronização:{" "}
                {format(new Date(integration.last_sync_at), "dd/MM/yyyy HH:mm", {
                  locale: ptBR,
                })}
              </p>
            )}

            {hasError && integration?.sync_status && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span>{integration.sync_status}</span>
              </div>
            )}

            {!isAvailable && (
              <Badge variant="secondary" className="mt-2">
                Em breve
              </Badge>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isConnected ? (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onSync(integration.id)}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Sincronizar dados com a integração agora</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={onConfigure}
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Configurar credenciais da integração</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDisconnect(integration.id)}
                  >
                    Desconectar
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Desconectar integração (mantém dados)</p>
                </TooltipContent>
              </Tooltip>
              <AlertDialog>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="ghost">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </AlertDialogTrigger>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Remover integração e excluir todos os dados</p>
                  </TooltipContent>
                </Tooltip>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remover integração?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esta ação não pode ser desfeita. Todos os dados de sincronização
                      serão perdidos.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onDelete(integration.id)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Remover
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="default"
                  disabled={!isAvailable}
                  onClick={onConnect}
                  className="gradient-primary"
                >
                  {integration?.status === "disconnected" ? "Reconectar" : "Conectar"}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{integration?.status === "disconnected" ? "Reconectar integração desconectada" : "Conectar nova integração"}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </Card>
    </TooltipProvider>
  );
}
